import { Button, Grid2, TextField, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { TFunction } from 'i18next';
import { useOrganizationPricing } from 'features/organizations/hooks/useOrganizationPricing';
import { useAuthState } from 'state/useAuthState';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';
import { OrganizationSeatsFormFields } from '../OrganizationSeatsFormFields/OrganizationSeatsFormFields';

type OrganizationAccountInfoData = {
  companyName: string;
  firstName: string;
  lastName: string;
  email: string;
  proSeats: number;
  regularSeats: number;
  additionalEmails?: string;
  poNumber?: string;
};

type OrganizationAccountInfoFormProps = {
  onSubmit: (values: Partial<OrganizationState>) => void;
};

const getValidationSchema = (t: TFunction): Yup.AnyObjectSchema => {
  return Yup.object()
    .shape({
      companyName: Yup.string().required(
        t('organizationPurchase.companyName.required'),
      ),
      firstName: Yup.string().required(
        t('organizationPurchase.firstName.required'),
      ),
      lastName: Yup.string().required(
        t('organizationPurchase.lastName.required'),
      ),
      email: Yup.string()
        .email(t('organizationPurchase.email.invalid'))
        .required(t('organizationPurchase.email.required')),
      proSeats: Yup.number()
        .typeError(t('organizationPurchase.proSeats.required'))
        .min(0, t('organizationPurchase.proSeats.min'))
        .required(t('organizationPurchase.proSeats.required')),
      regularSeats: Yup.number()
        .typeError(t('organizationPurchase.regularSeats.required'))
        .min(0, t('organizationPurchase.regularSeats.min'))
        .required(t('organizationPurchase.regularSeats.required')),
    })
    .test('seats-total', t('organizationPurchase.seats.total'), (values) => {
      return (values.proSeats || 0) + (values.regularSeats || 0) > 0;
    });
};

export const OrganizationAccountInfoForm = (
  props: OrganizationAccountInfoFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const { pricing } = useOrganizationPricing();
  const { companyName, firstName, lastName, email, proSeats, regularSeats } =
    useOrganizationState();
  const { user } = useAuthState();

  const { control, handleSubmit, watch, formState } =
    useForm<OrganizationAccountInfoData>({
      resolver: yupResolver(getValidationSchema(t)),
      defaultValues: {
        companyName: companyName || '',
        firstName: firstName || '',
        lastName: lastName || '',
        email: email || '',
        proSeats: proSeats || 0,
        regularSeats: regularSeats || 0,
      },
    });

  const { errors } = formState;

  const watchedRegularSeats = watch('regularSeats', regularSeats);
  const watchedProSeats = watch('proSeats', proSeats);

  const handleFormSubmit = (data: OrganizationAccountInfoData): void => {
    onSubmit(data);
  };

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleFormSubmit)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Controller
            name="companyName"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.companyName.label')}
                error={!!errors.companyName}
                helperText={errors.companyName?.message}
                required
                {...field}
              />
            )}
          />
        </Grid2>
        <Grid2 container spacing={2} direction="row">
          <Grid2 size={6}>
            <Controller
              name="firstName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.firstName.label')}
                  error={!!errors.firstName}
                  helperText={errors.firstName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Controller
              name="lastName"
              control={control}
              render={({ field }) => (
                <TextField
                  fullWidth
                  label={t('organizationPurchase.lastName.label')}
                  error={!!errors.lastName}
                  helperText={errors.lastName?.message}
                  required
                  {...field}
                />
              )}
            />
          </Grid2>
        </Grid2>
        <Grid2>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.email.label')}
                error={!!errors.email}
                helperText={errors.email?.message}
                required
                disabled={!!user?.email}
                {...field}
              />
            )}
          />
        </Grid2>
        <Grid2>
          <Controller
            name="poNumber"
            control={control}
            render={({ field }) => (
              <TextField
                fullWidth
                label={t('organizationPurchase.poNumber.label')}
                error={!!errors.poNumber}
                helperText={errors.poNumber?.message}
                {...field}
              />
            )}
          />
        </Grid2>

        <Grid2>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid2>

        <OrganizationSeatsFormFields
          control={control}
          formState={formState}
          pricing={pricing}
        />

        <OrganizationPurchaseSummary
          regularSeats={watchedRegularSeats}
          proSeats={watchedProSeats}
        />
      </Grid2>

      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Button size="large" fullWidth type="submit">
          {t('organizationPurchase.continue')}
        </Button>
      </Grid2>
    </Grid2>
  );
};
